import { applyTryBookingToSessions } from '@/data/trybookingEvents';

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export function formatDateKey(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatLongDate(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatSessionRange(session) {
  const startLabel = formatLongDate(session.date);
  const endLabel = session.endDate ? formatLongDate(session.endDate) : startLabel;
  if (endLabel === startLabel) {
    return `${startLabel} ${session.startTime} - ${session.endTime}`;
  }
  return `${startLabel} ${session.startTime} - ${endLabel} ${session.endTime}`;
}

export function formatPriceRange(session) {
  const min = Number(session.priceMin).toFixed(2);
  const max = Number(session.priceMax).toFixed(2);
  if (min === max) return `$${min}`;
  return `$${min} - $${max}`;
}

function parsePriceMin(priceLabel) {
  const match = String(priceLabel ?? '').match(/\$?\s*(\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : 25;
}

function addHours(date, hours) {
  const next = new Date(date);
  next.setHours(next.getHours() + hours);
  return next;
}

function formatTime(date) {
  return date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function sessionStatus(dateKey, explicitStatus) {
  if (explicitStatus === 'unavailable') return 'unavailable';
  const endOfDay = new Date(`${dateKey}T23:59:59`);
  if (endOfDay.getTime() < Date.now()) return 'past';
  return explicitStatus ?? 'available';
}

export function deriveSessionsFromEvent(event) {
  const start = new Date(event.startsAt);
  if (Number.isNaN(start.getTime())) return [];

  const dateKey = formatDateKey(start);
  const end = addHours(start, 3);
  const min = parsePriceMin(event.price);

  return [
    {
      id: `${event.id}-main`,
      date: dateKey,
      startTime: formatTime(start),
      endTime: formatTime(end),
      endDate: formatDateKey(end) !== dateKey ? formatDateKey(end) : undefined,
      status: sessionStatus(dateKey),
      priceMin: min,
      priceMax: Math.max(min + 20, Math.round(min * 2.5)),
      headline: event.subtitle || 'An unforgettable night awaits',
      tagline: `Experience ${event.title} at ${event.venue}`,
      description: `${event.title} — ${event.subtitle}. Presented by ${event.presenter}.`,
    },
  ];
}

export function getEventSessions(event) {
  let sessions;
  if (event?.sessions?.length) {
    sessions = event.sessions.map((session) => ({
      ...session,
      status: sessionStatus(session.date, session.status),
    }));
  } else {
    sessions = deriveSessionsFromEvent(event);
  }
  return applyTryBookingToSessions(event, sessions);
}

export function getInitialMonth(sessions) {
  const first = sessions.find((session) => session.status !== 'past') ?? sessions[0];
  if (!first) return new Date();
  const [year, month] = first.date.split('-').map(Number);
  return new Date(year, month - 1, 1);
}

export function buildCalendarWeeks(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstOfMonth.getDay() + 6) % 7;

  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

export function getDateAvailability(dateKey, sessionsByDate) {
  const daySessions = sessionsByDate.get(dateKey) ?? [];
  if (!daySessions.length) return null;
  if (daySessions.every((session) => session.status === 'past')) return 'past';
  if (daySessions.every((session) => session.status === 'unavailable')) return 'unavailable';
  if (daySessions.some((session) => session.status === 'available')) return 'available';
  return 'unavailable';
}

export function groupSessionsByDate(sessions) {
  const map = new Map();
  sessions.forEach((session) => {
    const list = map.get(session.date) ?? [];
    list.push(session);
    map.set(session.date, list);
  });
  return map;
}

export { DAY_LABELS };
